import React, { useState, useEffect, useMemo } from 'react';
import PageLayout from 'components/layouts/PageLayout';
import Modal from 'components/Modal';
import AlertError from 'components/AlertError';
import { validatePhone, formatPhoneNumber } from 'utils/validatePhone';
import setLocalStorage from 'utils/setLocalStorage';
import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import {
  US_STATES,
  calculatePlanQuotes
} from './data/rateData';
import {
  FaPhoneAlt,
  FaArrowRight,
  FaArrowLeft,
  FaCheckCircle,
  FaRedo,
  FaUser,
  FaUserFriends,
  FaChild,
  FaShieldAlt,
  FaCrown,
  FaChevronLeft,
  FaChevronRight,
  FaLock,
  FaExclamationCircle
} from 'react-icons/fa';
import { MdFamilyRestroom } from 'react-icons/md';
import TermsAndConditionContent from '../terms_and_condition/content/TermsAndConditionContent';
import PrivacyPolicyContent from '../privacy_policy/content/PrivacyPolicyContent';

const picture1Benefits = `${process.env.PUBLIC_URL || ''}/Picture 1 (Summary of benefits).jpg`;
const picture2Benefits = `${process.env.PUBLIC_URL || ''}/Picture 2 (Summary of benefits).jpg`;

const COVERAGE_OPTIONS = [
  {
    key: 'individual',
    label: 'Individual',
    sublabel: 'Just Me',
    icon: FaUser
  },
  {
    key: 'couple',
    label: 'Couple',
    sublabel: 'Me + Spouse',
    icon: FaUserFriends
  },
  {
    key: 'parent_children',
    label: 'Individual + Child(ren)',
    sublabel: 'Me + Kids',
    icon: FaChild
  },
  {
    key: 'family',
    label: 'Family',
    sublabel: 'Entire Family',
    icon: MdFamilyRestroom
  }
];

const TCPA_CALCULATOR_DISCLAIMER =
  "I agree to the Terms & Conditions, Privacy Policy, and Communication Agreement. By checking this box and clicking 'Reveal My Savings', I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. Consent is not a condition of purchase.";

const HealthPlanCalculator = () => {

  useEffect(() => {
    document.title = "Health Plan Calculator - Insurance Enterprise";
    window.scrollTo(0, 0);
  }, []);

  // Multi-step state: 1 = Demographics, 2 = Estimated Rates, 3 = Savings Result
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Demographics (Defaulting to empty values)
  const [demographics, setDemographics] = useState({
    state: '',
    zipCode: '',
    gender: '',
    familySetup: '',
    childrenCount: '',
    age: '',
    tobacco: ''
  });

  // Step 1: Validation State
  const [step1Errors, setStep1Errors] = useState({});

  // Step 2 -> 3 Gated Modal
  const [showModal, setShowModal] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalTab, setLegalTab] = useState('terms'); // 'terms' | 'privacy' | 'comm'

  const openLegalModal = (tab = 'terms') => {
    setLegalTab(tab);
    setShowLegalModal(true);
  };

  const [leadForm, setLeadForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    tcpaConsent: false
  });
  const [leadErrors, setLeadErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState('');
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [errorToastMsg, setErrorToastMsg] = useState('');

  // Compliance IP Detection (First-party WP endpoint + fallback)
  const [ipAddress, setIpAddress] = useState('Detecting...');

  useEffect(() => {
    // 1. Try first-party WordPress endpoint (immune to client-side ad-blockers)
    fetch('/wp-json/custom/v1/getIP')
      .then((res) => res.json())
      .then((data) => {
        if (data && data.ip && data.ip !== 'Unavailable') {
          setIpAddress(data.ip);
        } else {
          throw new Error('WP IP check failed');
        }
      })
      .catch(() => {
        // 2. Fallback to external IP service if running outside WP
        fetch('https://api64.ipify.org?format=json')
          .then((res) => res.json())
          .then((data) => {
            if (data && data.ip) setIpAddress(data.ip);
            else setIpAddress('127.0.0.1');
          })
          .catch(() => setIpAddress('127.0.0.1'));
      });
  }, []);

  const calculated = useMemo(() => {
    return calculatePlanQuotes({
      age: demographics.age || 35,
      familySetup: demographics.familySetup || 'individual',
      tobacco: demographics.tobacco || 'no',
      childrenCount: demographics.childrenCount || 0
    });
  }, [demographics]);

  const handleDemographicChange = (name, value) => {
    setDemographics((prev) => {
      const next = { ...prev, [name]: value };
      if (name === 'familySetup') {
        if (value !== 'parent_children' && value !== 'family') {
          next.childrenCount = '';
        }
      }
      return next;
    });
    if (step1Errors[name]) {
      setStep1Errors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const errors = {};
    if (!demographics.state) {
      errors.state = 'Please select a state.';
    }
    if (!demographics.zipCode.trim()) {
      errors.zipCode = 'Zip Code is required.';
    } else if (!/^\d{5}$/.test(demographics.zipCode.trim())) {
      errors.zipCode = 'Please enter a valid 5-digit zip code.';
    }
    if (!demographics.familySetup) {
      errors.familySetup = 'Please select a coverage type.';
    }
    if (demographics.familySetup === 'parent_children' || demographics.familySetup === 'family') {
      if (!demographics.childrenCount) {
        errors.childrenCount = 'Please select the number of children.';
      }
    }
    if (!demographics.age || demographics.age < 18 || demographics.age > 64) {
      errors.age = 'Please select applicant age.';
    }
    if (!demographics.gender) {
      errors.gender = 'Please select a gender.';
    }
    if (!demographics.tobacco) {
      errors.tobacco = 'Please select tobacco use status.';
    }
    setStep1Errors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLeadChange = (e) => {
    const { name, value, type, checked } = e.target;
    let finalValue = type === 'checkbox' ? checked : value;

    if (name === 'phone') {
      finalValue = formatPhoneNumber(value);
    } else if (name === 'firstName' || name === 'lastName') {
      // Only permit letters, spaces, hyphens, and apostrophes
      finalValue = value.replace(/[^a-zA-Z\s'-]/g, '');
    }

    setLeadForm((prev) => ({
      ...prev,
      [name]: finalValue
    }));
    if (leadErrors[name]) {
      setLeadErrors((prev) => ({ ...prev, [name]: '' }));
    }
    if (submissionError) {
      setSubmissionError('');
    }
  };

  const validateLead = () => {
    const newErrors = {};
    const trimmedFirst = leadForm.firstName.trim();
    const trimmedLast = leadForm.lastName.trim();
    const trimmedEmail = leadForm.email.trim();
    const trimmedPhone = leadForm.phone.trim();

    // 1. First Name Validation
    if (!trimmedFirst) {
      newErrors.firstName = 'First Name is required.';
    } else if (trimmedFirst.length < 2) {
      newErrors.firstName = 'First Name must be at least 2 characters.';
    } else if (!/^[a-zA-Z\s'-]+$/.test(trimmedFirst)) {
      newErrors.firstName = 'Please enter a valid first name (letters only).';
    }

    // 2. Last Name Validation (Optional, but if provided, must be valid name)
    if (trimmedLast && !/^[a-zA-Z\s'-]+$/.test(trimmedLast)) {
      newErrors.lastName = 'Please enter a valid last name (letters only).';
    }

    // 3. Phone Validation (Strict NANP 10-digit US mobile/landline)
    if (!trimmedPhone) {
      newErrors.phone = 'Phone number is required.';
    } else if (!validatePhone(trimmedPhone)) {
      newErrors.phone = 'Please enter a valid 10-digit US phone number (e.g. (555) 123-4567).';
    }

    // 4. Email Validation (Strict RFC pattern)
    if (!trimmedEmail) {
      newErrors.email = 'Email is required.';
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)) {
      newErrors.email = 'Please enter a valid email address (e.g. name@example.com).';
    }

    // 5. TCPA Consent
    if (!leadForm.tcpaConsent) {
      newErrors.tcpaConsent = 'Please agree to the terms and communication agreement to continue.';
    }

    setLeadErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Timeout promise wrapper helper
  const withTimeout = (promise, timeoutMs = 30000) => {
    let timeoutHandle;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutHandle = setTimeout(() => {
        reject(new Error('TIMEOUT_ERROR'));
      }, timeoutMs);
    });
    return Promise.race([promise, timeoutPromise]).finally(() => {
      clearTimeout(timeoutHandle);
    });
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setSubmissionError('');
    setShowErrorToast(false);

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const msg = 'No internet connection detected. Please check your network and try again.';
      setSubmissionError(msg);
      setErrorToastMsg(msg);
      setShowErrorToast(true);
      return;
    }

    if (!validateLead()) return;

    setIsSubmitting(true);

    const coverageLabels = {
      individual: 'Individual',
      couple: 'Couple',
      parent_children: 'Individual + Child(ren)',
      family: 'Family'
    };

    const cleanPhone = leadForm.phone.replace(/\D/g, '');

    const payload = {
      // 1. Standard Contact Fields
      firstName: leadForm.firstName.trim(),
      lastName: leadForm.lastName.trim(),
      name: `${leadForm.firstName.trim()} ${leadForm.lastName.trim()}`,
      email: leadForm.email.trim(),
      phone: leadForm.phone.trim(),
      phoneRaw: cleanPhone,
      state: demographics.state,
      zipCode: demographics.zipCode || '00000',

      // 2. Demographic & Rate Quote Fields
      age: parseInt(demographics.age, 10),
      gender: demographics.gender === 'male' ? 'Male' : 'Female',
      familySetup: coverageLabels[demographics.familySetup] || demographics.familySetup,
      coverageType: coverageLabels[demographics.familySetup] || demographics.familySetup,
      numberOfDependents: demographics.childrenCount ? parseInt(demographics.childrenCount, 10) : 0,
      childrenCount: demographics.childrenCount ? parseInt(demographics.childrenCount, 10) : 0,
      tobaccoStatus: demographics.tobacco === 'yes' ? 'Tobacco User' : 'Non-Tobacco',
      silverQuote: calculated.silverRate,
      goldQuote: calculated.goldRate,
      marketplacePrice: calculated.acaMarketplace,
      monthlySavings: calculated.monthlySavings,
      annualSavings: calculated.annualSavings,
      savingsPercentage: calculated.savingsPct,

      // 3. TCPA Compliance & Audit Logging Fields
      tcpaConsent: true,
      tcpaConsentCaptured: true,
      formName: 'Health Plan Calculator Engine',
      timestamp: new Date().toISOString(),
      ipAddress: ipAddress !== 'Detecting...' ? ipAddress : '0.0.0.0',
      formUrl: window.location.href,
      userAgent: navigator.userAgent,
      exactDisclaimerText: TCPA_CALCULATOR_DISCLAIMER
    };

    try {
      await withTimeout(
        (async () => {
          const [accountID, webhookID] = await Promise.all([
            getAccountID(),
            getWebhookID({ formName: 'calculator' })
          ]);
          await sendToGHL(payload, accountID, webhookID);
        })(),
        30000
      );

      // Pre-fill Enrollment Form in localStorage (matching Quote Form behavior)
      try {
        localStorage.removeItem('quoteForm');
        localStorage.removeItem('priceQuote');
        const quoteStorageData = {
          firstName: leadForm.firstName.trim(),
          lastName: leadForm.lastName.trim(),
          email: leadForm.email.trim(),
          phone: leadForm.phone.trim(),
          gender: demographics.gender === 'male' ? 'Male' : 'Female',
          age: parseInt(demographics.age, 10),
          state: demographics.state,
          zipCode: demographics.zipCode
        };
        setLocalStorage('quoteForm', JSON.stringify(quoteStorageData), 1000 * 60 * 60);
        setLocalStorage('priceQuote', JSON.stringify({ priceQuote: calculated.silverRate, healthPlan: 'silver' }), 1000 * 60 * 60);
      } catch (storageErr) {
        console.warn('LocalStorage save failed:', storageErr);
      }

      // Clear Lead Form Fields after submitting
      setLeadForm({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        tcpaConsent: false
      });
      setLeadErrors({});
      setSubmissionError('');

      setShowModal(false);
      setShowSuccessToast(true);
      setTimeout(() => setShowSuccessToast(false), 4500);
      setCurrentStep(3);
      window.scrollTo({ top: 100, behavior: 'smooth' });
    } catch (err) {
      console.error('GHL Submission Failed:', err);
      let userMsg = 'Unable to submit your quote at this time. Please check your connection or try again.';
      if (err?.message === 'TIMEOUT_ERROR' || err?.code === 'ECONNABORTED' || err?.message?.toLowerCase().includes('timeout')) {
        userMsg = 'Request timed out after 30 seconds. Please check your internet connection and try again.';
      } else if (err?.message === 'OFFLINE_ERROR' || (typeof navigator !== 'undefined' && !navigator.onLine)) {
        userMsg = 'No internet connection detected. Please check your network connection and try again.';
      } else if (err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK') {
        userMsg = 'Network connection issue. Please verify your internet connection and try again.';
      } else if (err?.message?.includes('Account ID') || err?.message?.includes('Webhook ID')) {
        userMsg = 'Configuration server unreachable. Please make sure the backend service is running or try again later.';
      } else if (err?.response?.status >= 500) {
        userMsg = 'The server encountered an error while processing your request. Please try again shortly.';
      } else if (err?.message) {
        userMsg = err.message;
      }
      setSubmissionError(userMsg);
      setErrorToastMsg(userMsg);
      setShowErrorToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProceedToStep2 = () => {
    const missing = [];
    if (!demographics.state) missing.push('State');
    if (!demographics.zipCode.trim() || !/^\d{5}$/.test(demographics.zipCode.trim())) missing.push('5-digit Zip Code');
    if (!demographics.familySetup) missing.push('Coverage Setup');
    if ((demographics.familySetup === 'parent_children' || demographics.familySetup === 'family') && !demographics.childrenCount) {
      missing.push('Number of Children');
    }
    if (!demographics.age || demographics.age < 18 || demographics.age > 64) missing.push('Age (18-64)');
    if (!demographics.gender) missing.push('Gender');
    if (!demographics.tobacco) missing.push('Tobacco Status');

    if (missing.length > 0) {
      validateStep1();
      const listStr = missing.slice(0, 3).join(', ') + (missing.length > 3 ? ` and ${missing.length - 3} more` : '');
      setErrorToastMsg(`Please complete: ${listStr}`);
      setShowErrorToast(true);
      window.scrollTo({ top: 150, behavior: 'smooth' });
      return;
    }

    setCurrentStep(2);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleBackToStep1 = () => {
    setCurrentStep(1);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  const handleRecalculate = () => {
    setDemographics({
      state: '',
      zipCode: '',
      gender: '',
      familySetup: '',
      childrenCount: '',
      age: '',
      tobacco: ''
    });
    setStep1Errors({});
    setLeadForm({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      tcpaConsent: false
    });
    setLeadErrors({});
    setCurrentStep(1);
    window.scrollTo({ top: 150, behavior: 'smooth' });
  };

  return (
    <div className="bg-slate-50 min-h-screen py-6 sm:py-10 pb-16 sm:pb-20 px-2 sm:px-4 overflow-x-clip">
      <PageLayout
        content={
          <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
            
            {/* Title Header */}
            <div className="text-center space-y-2 max-w-3xl mx-auto px-2">
              <h1 className="text-2xl sm:text-4xl md:text-5xl font-black text-base-content tracking-tight uppercase leading-tight">
                Health Insurance Savings Calculator
              </h1>
              <p className="text-xs sm:text-sm text-base-content/70">
                Estimate your annual rate and see how much you could save vs. Marketplace plans.
              </p>
            </div>

            {/* DaisyUI Steps Navigation Bar */}
            <div className="w-full max-w-2xl mx-auto px-2">
              <ul className="steps steps-horizontal w-full text-[11px] sm:text-xs font-semibold select-none">
                <li 
                  className={`step ${
                    currentStep >= 1 ? 'step-secondary font-bold text-secondary' : 'text-base-content/60'
                  }`}
                >
                  Your Details
                </li>
                <li 
                  className={`step ${
                    currentStep >= 2 ? 'step-secondary font-bold text-secondary' : 'text-base-content/60'
                  }`}
                >
                  Estimated Rates
                </li>
                <li 
                  className={`step ${
                    currentStep >= 3 ? 'step-secondary font-bold text-secondary' : 'text-base-content/60'
                  }`}
                >
                  Savings Breakdown
                </li>
              </ul>
            </div>

            {/* ================= OVERLAPPING CAROUSEL TRACK ================= */}
            <div className="relative w-full py-2 sm:py-4 overflow-x-clip">

              {/* Carousel Left Navigation Arrow (Go back from Step 2 to Step 1) */}
              {currentStep === 2 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleBackToStep1();
                  }}
                  className="absolute left-0 sm:left-2 md:left-4 top-1/2 -translate-y-1/2 z-50 btn btn-circle btn-sm sm:btn-md bg-white/95 hover:bg-white shadow-2xl border-2 border-slate-200 text-slate-700 hover:text-[#2AA107] hover:scale-110 active:scale-95 transition-all flex items-center justify-center cursor-pointer pointer-events-auto"
                  title="Previous Step"
                  aria-label="Previous Step"
                >
                  <FaChevronLeft className="text-xs sm:text-sm" />
                </button>
              )}

              {/* Carousel Right Navigation Arrow (Proceed to next step or unlock analysis) */}
              {currentStep < 3 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (currentStep === 1) {
                      handleProceedToStep2();
                    } else if (currentStep === 2) {
                      setShowModal(true);
                    }
                  }}
                  className="absolute right-0 sm:right-2 md:right-4 top-1/2 -translate-y-1/2 z-50 btn btn-circle btn-sm sm:btn-md bg-white/95 hover:bg-white shadow-2xl border-2 border-slate-200 text-slate-700 hover:text-[#2AA107] hover:scale-110 active:scale-95 transition-all flex items-center justify-center cursor-pointer pointer-events-auto"
                  title={currentStep === 1 ? 'See Estimated Rates' : 'Compare to Marketplace Price'}
                  aria-label={currentStep === 1 ? 'See Estimated Rates' : 'Compare to Marketplace Price'}
                >
                  <FaChevronRight className="text-xs sm:text-sm" />
                </button>
              )}

              {/* Stacked Carousel Deck (Fluid dynamic height on mobile, stacked on desktop) */}
              <div className="relative w-full transition-all duration-500 pb-2">

                {/* --- SLIDE 1: DEMOGRAPHICS FORM CARD --- */}
                <div 
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 1
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 2
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[70%] sm:-translate-x-[70%] md:-translate-x-[68%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep === 2) {
                      setCurrentStep(1);
                      window.scrollTo({ top: 150, behavior: 'smooth' });
                    }
                  }}
                >
                  {currentStep === 1 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-base-200 pb-3">
                        <div className="flex items-center space-x-2">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0 shadow-xs">Step 1</span>
                          <h2 className="text-base font-bold text-base-content">
                            Enter Your Details
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium">1 of 3</span>
                      </div>

                      {/* State & Zip */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              State <span className="text-error">*</span>
                            </span>
                          </label>
                          <select
                            className={`select select-bordered select-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none ${
                              step1Errors.state ? 'select-error' : ''
                            }`}
                            value={demographics.state}
                            onChange={(e) => handleDemographicChange('state', e.target.value)}
                          >
                            <option value="">Select State</option>
                            {US_STATES.map((s) => (
                              <option key={s.code} value={s.code}>
                                {s.name} ({s.code})
                              </option>
                            ))}
                          </select>
                          {step1Errors.state && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.state}</span>
                          )}
                        </div>

                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Zip Code <span className="text-error">*</span>
                            </span>
                          </label>
                          <input
                            type="text"
                            maxLength={5}
                            placeholder="e.g. 90210"
                            className={`input input-bordered input-sm w-full text-xs focus:border-[#2AA107] focus:ring-1 focus:ring-[#2AA107] focus:outline-none ${
                              step1Errors.zipCode ? 'input-error' : ''
                            }`}
                            value={demographics.zipCode}
                            onChange={(e) => handleDemographicChange('zipCode', e.target.value.replace(/\D/g, '').slice(0, 5))}
                          />
                          {step1Errors.zipCode && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.zipCode}</span>
                          )}
                        </div>
                      </div>

                      {/* Coverage Type Grid */}
                      <div>
                        <label className="label py-1">
                          <span className="label-text font-semibold text-xs text-base-content">
                            Who Needs Coverage? <span className="text-error">*</span>
                          </span>
                        </label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                          {COVERAGE_OPTIONS.map((opt) => {
                            const IconComp = opt.icon;
                            const isSelected = demographics.familySetup === opt.key;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                onClick={() => handleDemographicChange('familySetup', opt.key)}
                                className={`p-3 rounded-xl border text-center transition-all flex flex-col items-center justify-center space-y-1 ${
                                  isSelected
                                    ? 'border-[#2AA107] bg-[#2AA107]/10 text-[#2AA107] font-bold shadow-sm ring-1 ring-[#2AA107]'
                                    : 'border-base-300 bg-base-100 hover:border-[#2AA107]/40 text-base-content'
                                }`}
                              >
                                <IconComp className="text-lg" />
                                <span className="text-xs font-semibold leading-tight">{opt.label}</span>
                                <span className={`text-[10px] ${isSelected ? 'text-[#2AA107]/80' : 'text-base-content/60'}`}>{opt.sublabel}</span>
                              </button>
                            );
                          })}
                        </div>
                        {step1Errors.familySetup && (
                          <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.familySetup}</span>
                        )}
                      </div>

                      {/* Number of Children Selector (Shown when Individual + Child(ren) or Family is selected) */}
                      {(demographics.familySetup === 'parent_children' || demographics.familySetup === 'family') && (
                        <div className="bg-emerald-50/50 border border-[#2AA107]/20 p-3.5 rounded-2xl space-y-2 animate-fadeIn">
                          <label className="label-text font-bold text-xs text-slate-800 flex items-center gap-1.5">
                            <span>Number of Children</span>
                            <span className="text-error">*</span>
                          </label>

                          {/* Children count button grid 1 to 7 */}
                          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2 pt-1">
                            {[1, 2, 3, 4, 5, 6, 7].map((num) => {
                              const isSelected = parseInt(demographics.childrenCount, 10) === num;
                              return (
                                <button
                                  key={num}
                                  type="button"
                                  onClick={() => handleDemographicChange('childrenCount', num)}
                                  className={`py-2 rounded-xl border text-xs font-bold transition-all text-center ${
                                    isSelected
                                      ? 'border-[#2AA107] bg-[#2AA107] text-white shadow-md'
                                      : 'border-base-300 bg-white hover:border-[#2AA107]/50 text-base-content shadow-xs'
                                  }`}
                                >
                                  {num}
                                </button>
                              );
                            })}
                          </div>
                          {step1Errors.childrenCount && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.childrenCount}</span>
                          )}
                        </div>
                      )}

                      {/* Age Slider (18 to 64) */}
                      <div>
                        <div className="flex items-center justify-between py-1">
                          <label className="label-text font-semibold text-xs text-base-content">
                            Applicant Age <span className="text-error">*</span>
                          </label>
                          {demographics.age ? (
                            <span className="bg-[#2AA107] text-white text-xs font-bold px-2 py-0.5 rounded-md shadow-xs">
                              {demographics.age} years old
                            </span>
                          ) : (
                            <span className="text-base-content/50 font-normal italic text-xs">
                              (Slide to select age)
                            </span>
                          )}
                        </div>
                        <input
                          type="range"
                          min="18"
                          max="64"
                          value={demographics.age || 18}
                          onChange={(e) => handleDemographicChange('age', parseInt(e.target.value, 10))}
                          className="w-full h-2 rounded-lg appearance-none cursor-pointer mt-1"
                          style={{
                            accentColor: '#2AA107',
                            background: demographics.age
                              ? `linear-gradient(to right, #2AA107 0%, #2AA107 ${(((demographics.age) - 18) / 46) * 100}%, #e2e8f0 ${(((demographics.age) - 18) / 46) * 100}%, #e2e8f0 100%)`
                              : '#e2e8f0'
                          }}
                        />
                        {/* Exact percentage-aligned tick marks */}
                        <div className="relative w-full text-[10px] text-base-content/60 font-semibold h-4 mt-1 select-none">
                          {[18, 25, 35, 45, 55, 64].map((tAge) => {
                            const pct = ((tAge - 18) / 46) * 100;
                            const isFirst = tAge === 18;
                            const isLast = tAge === 64;
                            return (
                              <span
                                key={tAge}
                                className={`absolute cursor-pointer hover:text-[#2AA107] transition-colors ${
                                  isFirst ? 'left-0' : isLast ? 'right-0' : '-translate-x-1/2'
                                }`}
                                style={!isFirst && !isLast ? { left: `${pct}%` } : {}}
                                onClick={() => handleDemographicChange('age', tAge)}
                              >
                                {tAge} yrs
                              </span>
                            );
                          })}
                        </div>
                        {step1Errors.age && (
                          <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.age}</span>
                        )}
                      </div>

                      {/* Gender & Tobacco Radio Groups */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Gender <span className="text-error">*</span>
                            </span>
                          </label>
                          <div className="flex space-x-2 pt-1">
                            {['male', 'female'].map((g) => (
                              <button
                                key={g}
                                type="button"
                                onClick={() => handleDemographicChange('gender', g)}
                                className={`flex-1 py-2 px-3 rounded-lg border text-xs capitalize font-bold transition-all ${
                                  demographics.gender === g
                                    ? 'border-[#2AA107] bg-[#2AA107] text-white shadow-sm'
                                    : 'border-base-300 bg-base-100 hover:border-[#2AA107]/40 text-base-content'
                                }`}
                              >
                                {g}
                              </button>
                            ))}
                          </div>
                          {step1Errors.gender && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.gender}</span>
                          )}
                        </div>

                        <div>
                          <label className="label py-1">
                            <span className="label-text font-semibold text-xs text-base-content">
                              Tobacco User? <span className="text-error">*</span>
                            </span>
                          </label>
                          <div className="flex space-x-2 pt-1">
                            {[
                              { value: 'no', label: 'No' },
                              { value: 'yes', label: 'Yes' }
                            ].map((t) => (
                              <button
                                key={t.value}
                                type="button"
                                onClick={() => handleDemographicChange('tobacco', t.value)}
                                className={`flex-1 py-2 px-3 rounded-lg border text-xs font-bold transition-all ${
                                  demographics.tobacco === t.value
                                    ? 'border-[#2AA107] bg-[#2AA107] text-white shadow-sm'
                                    : 'border-base-300 bg-base-100 hover:border-[#2AA107]/40 text-base-content'
                                }`}
                              >
                                {t.label}
                              </button>
                            ))}
                          </div>
                          {step1Errors.tobacco && (
                            <span className="text-xs text-error mt-1 block font-semibold">{step1Errors.tobacco}</span>
                          )}
                        </div>
                      </div>

                      {/* Step 1 Submit Button */}
                      <div className="pt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProceedToStep2();
                          }}
                          className="btn w-full bg-[#2AA107] hover:bg-[#238806] text-white font-bold uppercase text-xs sm:text-sm tracking-wider shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all border-0 cursor-pointer"
                        >
                          <span>See Estimated Rates</span>
                          <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Step 1 Peeking Summary Card (When on Step 2) */
                    <div 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleBackToStep1();
                      }}
                      className="flex flex-col justify-between h-full space-y-4 py-1 text-left cursor-pointer"
                    >
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge bg-[#2AA107] badge-sm text-white font-bold border-0">✓ Step 1</span>
                        <span className="text-xs text-[#2AA107] font-bold">Edit ✎</span>
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Your Details</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs text-base-content/85">
                          <p><strong>Location:</strong> {demographics.state || 'FL'}, {demographics.zipCode || '33101'}</p>
                          <p><strong>Age:</strong> {demographics.age || '35'} yrs ({demographics.gender || 'Female'})</p>
                          <p>
                            <strong>Setup:</strong> {COVERAGE_OPTIONS.find(c => c.key === demographics.familySetup)?.label || 'Individual'}
                            {(demographics.familySetup === 'parent_children' || demographics.familySetup === 'family') && demographics.childrenCount > 0 && ` (${demographics.childrenCount} ${demographics.childrenCount === 1 ? 'Child' : 'Children'})`}
                          </p>
                          <p><strong>Tobacco:</strong> {demographics.tobacco === 'yes' ? 'Yes' : 'No'}</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline border-[#2AA107] text-[#2AA107] hover:bg-[#2AA107] hover:text-white btn-xs font-bold w-full">Edit Details ✎</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 2: ESTIMATED RATES CARD --- */}
                <div 
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 2
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-base-200 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 1
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[30%] sm:-translate-x-[30%] md:-translate-x-[32%] scale-[0.92] opacity-50 z-10 shadow-lg bg-base-100 border-base-300 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[90%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep === 1) {
                      handleProceedToStep2();
                    }
                  }}
                >
                  {currentStep === 2 ? (
                    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-base-200 pb-3">
                        <div className="flex items-center space-x-2">
                          <span className="badge bg-[#2AA107] text-white badge-sm font-bold border-0">Step 2</span>
                          <h2 className="text-sm sm:text-base font-bold text-base-content">
                            Your Estimated Plan Rates
                          </h2>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium">2 of 3</span>
                      </div>

                      {/* Plan Cards: Both Silver and Gold in Equal Size */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 items-stretch pt-4">
                        {/* Silver Plan Card (With Floating Top Recommended Badge) */}
                        <div className="relative border-2 border-slate-300 rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-gradient-to-b from-white via-slate-50 to-slate-100 shadow-md transition-all space-y-4 flex flex-col justify-between pt-6 sm:pt-7">
                          {/* Sleek Floating Top Badge */}
                          <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#2AA107] text-white text-[10px] sm:text-[11px] font-black uppercase tracking-wider px-3.5 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                            RECOMMENDED
                          </span>

                          <div className="space-y-3">
                            <div>
                              <span className="inline-block bg-gradient-to-r from-slate-700 to-slate-800 text-white text-xs uppercase font-black tracking-widest px-4 py-1 rounded-full shadow-xs">
                                SILVER PLAN
                              </span>
                            </div>
                            <div className="py-1">
                              <div className="text-4xl sm:text-5xl font-black text-slate-800 tracking-tight leading-none">
                                ${calculated.silverRate}
                              </div>
                              <span className="text-xs sm:text-sm font-semibold text-slate-600 mt-1 block">per month</span>
                            </div>
                          </div>
                          <div className="pt-3 border-t border-slate-200 flex justify-center items-center">
                            <div className="w-8 h-8 rounded-full bg-slate-200/80 border border-slate-300 flex items-center justify-center text-slate-600 shadow-inner">
                              <FaShieldAlt className="text-sm" />
                            </div>
                          </div>
                        </div>

                        {/* Gold Plan Card (Equal Size & Pure Symmetrical Alignment) */}
                        <div className="relative border-2 border-amber-200/90 rounded-2xl sm:rounded-3xl p-5 sm:p-6 text-center bg-gradient-to-b from-amber-50/30 via-white to-slate-50 shadow-md transition-all space-y-4 flex flex-col justify-between pt-6 sm:pt-7">
                          <div className="space-y-3">
                            <div>
                              <span className="inline-block bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs uppercase font-black tracking-widest px-4 py-1 rounded-full shadow-xs">
                                GOLD PLAN
                              </span>
                            </div>
                            <div className="py-1">
                              <div className="text-4xl sm:text-5xl font-black text-amber-900 tracking-tight leading-none">
                                ${calculated.goldRate}
                              </div>
                              <span className="text-xs sm:text-sm font-semibold text-amber-700/80 mt-1 block">per month</span>
                            </div>
                          </div>
                          <div className="pt-3 border-t border-amber-200/80 flex justify-center items-center">
                            <div className="w-8 h-8 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-700 shadow-inner">
                              <FaCrown className="text-xs" />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Step 2 Action Buttons */}
                      <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleBackToStep1();
                          }}
                          className="btn btn-outline btn-sm w-full sm:w-1/3 text-xs cursor-pointer"
                        >
                          <FaArrowLeft />
                          <span>Edit Details</span>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowModal(true);
                          }}
                          className="btn btn-sm w-full sm:flex-1 bg-[#2AA107] hover:bg-[#238806] text-white font-black uppercase text-xs tracking-wider border-0 shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all cursor-pointer py-2.5 h-auto"
                        >
                          <span>REVEAL MY SAVINGS</span>
                          <FaArrowRight />
                        </button>
                      </div>
                    </div>
                  ) : currentStep === 1 ? (
                    /* Step 2 Preview Teaser Cover (When on Step 1) */
                    <div className="flex flex-col justify-between h-full space-y-4 py-2 text-center">
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge badge-ghost badge-sm font-bold">Step 2</span>
                        <span className="text-xs text-base-content/50 font-medium">Up Next →</span>
                      </div>
                      <div className="space-y-3 py-4">
                        <div className="flex justify-center space-x-3">
                          <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 shadow-inner">
                            <FaShieldAlt className="text-base" />
                          </div>
                          <div className="w-10 h-10 rounded-full bg-amber-200 flex items-center justify-center text-amber-700 shadow-inner">
                            <FaCrown className="text-base" />
                          </div>
                        </div>
                        <h3 className="text-base font-bold text-base-content">Estimated Rates</h3>
                        <p className="text-xs text-base-content/60">Silver &amp; Gold Plan Rates</p>
                      </div>
                      <div className="pt-2 border-t border-base-200">
                        <span className="text-xs text-primary font-bold">Complete Step 1 to View</span>
                      </div>
                    </div>
                  ) : (
                    /* Step 2 Peeking Summary Cover (When on Step 3) */
                    <div className="flex flex-col justify-between h-full space-y-4 py-1 text-left">
                      <div className="flex items-center justify-between border-b border-base-200 pb-2.5">
                        <span className="badge badge-secondary badge-sm text-white font-bold">✓ Step 2</span>
                        <span className="text-xs text-primary font-bold">View Rates ✎</span>
                      </div>
                      <div className="space-y-2 py-2">
                        <h3 className="text-base font-bold text-base-content">Estimated Rates</h3>
                        <div className="bg-base-200/70 p-3 rounded-xl space-y-1 text-xs">
                          <p className="font-bold text-slate-800">Silver Plan: ${calculated.silverRate}/mo</p>
                          <p className="font-bold text-amber-900">Gold Plan: ${calculated.goldRate}/mo</p>
                        </div>
                      </div>
                      <div className="pt-2 border-t border-base-200 text-center">
                        <span className="btn btn-outline btn-primary btn-xs font-bold w-full">View Rates ✎</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* --- SLIDE 3: SAVINGS BREAKDOWN RESULTS CARD --- */}
                <div 
                  className={`w-full max-w-xl sm:max-w-2xl rounded-2xl sm:rounded-3xl border transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] transform ${
                    currentStep === 3
                      ? 'relative mx-auto scale-100 opacity-100 z-30 shadow-2xl bg-base-100 border-2 border-[#2AA107]/40 p-4 sm:p-6 md:p-8 pointer-events-auto filter blur-0'
                      : currentStep === 2
                      ? 'hidden sm:block absolute top-0 left-1/2 -translate-x-[30%] sm:-translate-x-[30%] md:-translate-x-[32%] scale-[0.92] opacity-65 z-10 shadow-xl bg-gradient-to-b from-[#2AA107]/10 to-white border-2 border-[#2AA107]/40 p-4 sm:p-6 md:p-8 cursor-pointer pointer-events-auto select-none ring-2 ring-[#2AA107]/20 filter blur-[1px]'
                      : 'hidden sm:block absolute top-0 left-1/2 -translate-x-[10%] scale-75 opacity-0 z-0 pointer-events-none filter blur-[4px]'
                  }`}
                  onClick={() => {
                    if (currentStep === 1) {
                      if (validateStep1()) {
                        setCurrentStep(2);
                        window.scrollTo({ top: 150, behavior: 'smooth' });
                      }
                    } else if (currentStep === 2) {
                      setShowModal(true);
                    }
                  }}
                >
                  {currentStep === 3 ? (
                    <div className="space-y-5 animate-fadeIn">
                      <div className="flex items-center justify-between border-b border-base-200 pb-3">
                        <div className="inline-flex items-center space-x-1.5 text-[#2AA107] text-xs font-bold uppercase">
                          <FaCheckCircle />
                          <span>Savings Analysis Complete</span>
                        </div>
                        <span className="text-xs text-base-content/60 font-medium">3 of 3</span>
                      </div>

                      {/* Hero Savings Callout with Interactive Monthly Hover Chip */}
                      <div className="group bg-gradient-to-b from-[#2AA107]/10 via-white to-slate-50 border-2 border-[#2AA107]/40 rounded-2xl p-5 sm:p-6 text-center shadow-md space-y-2.5 cursor-default select-none transition-all">
                        <p className="text-xs uppercase font-extrabold tracking-widest text-slate-500">
                          YOU SAVE AN ESTIMATED
                        </p>

                        {/* Interactive Hero Savings Number: Hover reveals the monthly savings chip */}
                        <div className="relative inline-flex items-center justify-center py-1">
                          <span className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#2AA107] tracking-tight leading-none transition-transform duration-300 group-hover:scale-[1.02]">
                            ${calculated.annualSavings.toLocaleString()}
                          </span>

                          <div className="ml-2 text-left flex flex-col items-start justify-center">
                            {/* Monthly Chip: Smoothly fades in and scales on hover */}
                            <div className="h-6 flex items-center">
                              <span className="opacity-0 group-hover:opacity-100 scale-90 group-hover:scale-100 transition-all duration-300 inline-flex items-center bg-[#2AA107] text-white text-[11px] sm:text-xs font-black px-2.5 py-0.5 rounded-full shadow-md -rotate-2 whitespace-nowrap">
                                ${calculated.monthlySavings}/mo
                              </span>
                            </div>
                            
                            {/* / Year Label */}
                            <span className="text-base sm:text-xl font-bold text-slate-700 leading-tight">
                              / Year
                            </span>
                          </div>
                        </div>

                        <div className="pt-1">
                          <span className="inline-flex items-center bg-[#2AA107] text-white text-xs sm:text-sm font-black px-4 py-1.5 rounded-full shadow-md tracking-wide">
                            {calculated.savingsPct}% LESS COMPARED TO MARKETPLACE RATES
                          </span>
                        </div>
                      </div>

                      {/* Rate Comparison Table */}
                      <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 shadow-sm space-y-3 text-left">
                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-wider border-b border-slate-100 pb-2">
                          Rate Comparison
                        </h4>
                        
                        <div className="space-y-2 text-xs sm:text-sm">
                          {/* ACA Marketplace Rate */}
                          <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-600">
                            <span className="font-semibold">ACA Marketplace rate</span>
                            <span className="font-bold text-slate-400 line-through text-sm sm:text-base">
                              ${calculated.acaMarketplace}/mo*
                            </span>
                          </div>

                          {/* Our Silver Plan (Recommended) */}
                          <div className="flex items-center justify-between p-2.5 rounded-xl bg-emerald-50/80 border-2 border-[#2AA107]/40 text-slate-800">
                            <div className="flex items-center space-x-2">
                              <span className="font-bold">Our Silver Plan</span>
                              <span className="inline-flex items-center bg-[#2AA107] text-white text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-full shadow-xs whitespace-nowrap">
                                Recommended
                              </span>
                            </div>
                            <span className="font-black text-[#2AA107] text-base sm:text-lg">
                              ${calculated.silverRate}/mo
                            </span>
                          </div>

                          {/* Our Gold Plan */}
                          <div className="flex items-center justify-between p-2.5 rounded-xl bg-amber-50/60 border border-amber-200 text-slate-800">
                            <span className="font-bold">Our Gold Plan</span>
                            <span className="font-extrabold text-amber-900 text-base sm:text-lg">
                              ${calculated.goldRate}/mo
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Summary of Benefits Pictures from public folder */}
                      <div className="space-y-3 text-left">
                        <h4 className="text-xs font-black text-slate-500 uppercase tracking-wider">
                          Summary of Benefits
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                          <div className="bg-white p-2.5 rounded-2xl border border-slate-200 shadow-sm text-center">
                            <img 
                              src={picture1Benefits} 
                              alt="Silver Plan Benefits Summary" 
                              className="w-full h-auto rounded-xl border border-slate-100 hover:scale-[1.01] transition-transform" 
                            />
                          </div>
                          <div className="bg-white p-2.5 rounded-2xl border border-slate-200 shadow-sm text-center">
                            <img 
                              src={picture2Benefits} 
                              alt="Gold Plan Benefits Summary" 
                              className="w-full h-auto rounded-xl border border-slate-100 hover:scale-[1.01] transition-transform" 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Step 3 Main Action CTA Button */}
                      <div className="space-y-3 pt-2">
                        <a
                          href="/enrollment"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn w-full bg-[#2AA107] hover:bg-[#238806] text-white font-black uppercase text-sm sm:text-base tracking-wider shadow-lg hover:scale-[1.01] active:scale-[0.99] transition-all border-0 py-3.5 sm:py-4 h-auto cursor-pointer"
                        >
                          <div className="flex flex-col items-center leading-tight">
                            <span>ENROLL NOW</span>
                            <span className="text-[11px] font-semibold opacity-95 tracking-normal">AND SECURE MY SAVINGS</span>
                          </div>
                        </a>

                        <div className="text-center pt-1">
                          <a
                            href="tel:8883506605"
                            className="inline-flex items-center space-x-2 text-xs sm:text-sm font-bold text-[#2AA107] hover:underline"
                          >
                            <FaPhoneAlt className="text-xs" />
                            <span>More questions? Call 888-350-6605</span>
                          </a>
                        </div>
                      </div>


                      {/* Bottom Benchmark Disclaimer & Re-calculate */}
                      <div className="text-center pt-3 space-y-2 border-t border-slate-200">
                        <p className="text-[11px] text-base-content/50 italic">
                          * Calculated against the average unsubsidized Marketplace benchmark rate.
                        </p>
                        <div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRecalculate();
                            }}
                            className="btn btn-ghost btn-xs text-base-content/60 hover:text-base-content font-bold cursor-pointer"
                          >
                            <FaRedo className="text-xs" />
                            <span>Recalculate</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Step 3 Enticing Teaser Preview Card (When on Step 1 or 2) */
                    <div className="flex flex-col justify-between h-full space-y-3 py-1 text-center select-none">
                      <div className="flex items-center justify-between border-b border-[#2AA107]/30 pb-2">
                        <span className="badge bg-[#2AA107] text-white badge-sm font-extrabold shadow-sm border-0">
                          ✨ Step 3
                        </span>
                        <span className="text-[11px] font-black text-[#2AA107] uppercase tracking-wider">
                          Huge Savings
                        </span>
                      </div>

                      <div className="space-y-2 py-1">
                        <div>
                          <span className="inline-block bg-[#2AA107] text-white text-[10px] uppercase font-black tracking-widest px-3 py-0.5 rounded-full shadow-sm">
                            Potential Annual Savings
                          </span>
                        </div>
                        
                        {/* Partially Revealed Savings Teaser */}
                        <div className="py-1">
                          <div className="text-3xl sm:text-4xl font-black text-[#2AA107] tracking-tight leading-none">
                            ${calculated.annualSavings > 0 ? calculated.annualSavings.toLocaleString().slice(0, -2) + '??' : '1?,???'}
                            <span className="text-xs font-bold text-slate-700"> / yr</span>
                          </div>
                          <div className="mt-1">
                            <span className="inline-block bg-[#2AA107] text-white text-[10px] font-black px-2.5 py-0.5 rounded-full shadow-xs">
                              Save Up to {calculated.savingsPct}%
                            </span>
                          </div>
                        </div>

                        {/* Focused Silver Plan Pill */}
                        <div className="bg-[#2AA107]/10 border border-[#2AA107]/20 p-2 rounded-xl text-center text-xs">
                          <span className="text-[10px] text-base-content/60 font-bold uppercase block">Our Silver Plan</span>
                          <span className="font-extrabold text-primary text-sm">${calculated.silverRate}/mo</span>
                        </div>
                      </div>

                      {/* Enticing CTA bottom lock bar */}
                      <div className="pt-2 border-t border-[#2AA107]/30 flex items-center justify-center space-x-1.5 text-xs text-[#2AA107] font-black">
                        <FaLock className="text-xs" />
                        <span>Complete Steps to Unlock Analysis →</span>
                      </div>
                    </div>
                  )}
                </div>

              </div>

              {/* Carousel Indicators / Step Dots (Visual Indicator Only) */}
              <div className="flex items-center justify-center space-x-2.5 pt-6 select-none">
                {[1, 2, 3].map((stepNum) => (
                  <div
                    key={stepNum}
                    className={`h-3 rounded-full transition-all duration-300 ${
                      currentStep === stepNum
                        ? 'w-8 bg-[#2AA107]'
                        : 'w-3 bg-slate-300'
                    }`}
                  />
                ))}
              </div>

            </div>

          </div>
        }
      />

      {/* Gated Lead Capture Modal (Triggered from Step 2) */}
      <Modal
        open={showModal}
        handleClose={() => {
          if (!isSubmitting) setShowModal(false);
        }}
        disableBackgroundClose={isSubmitting}
        closeButton={!isSubmitting}
        maxWidth="sm:max-w-md"
      >
        <div className="p-4 sm:p-6 text-left w-full mx-auto space-y-4">
          <div className="border-b border-base-200 pb-2">
            <h3 className="text-base font-bold text-base-content uppercase tracking-wider">
              REVEAL MY SAVINGS
            </h3>
            <p className="text-xs text-base-content/70">
              Enter your contact details to generate your potential annual savings report.
            </p>
          </div>

          {submissionError && (
            <div className="alert alert-error text-xs text-white p-3 rounded-xl flex items-start space-x-2.5 shadow-md animate-fadeIn">
              <FaExclamationCircle className="text-base shrink-0 mt-0.5" />
              <div className="flex-1 space-y-1">
                <p className="font-bold text-xs sm:text-sm">Submission Problem</p>
                <p className="text-[11px] opacity-95 leading-relaxed">{submissionError}</p>
                <p className="text-[10px] opacity-85 italic">Please verify your details and click submit to try again.</p>
              </div>
              <button
                type="button"
                aria-label="Close error message"
                title="Close error message"
                onClick={() => setSubmissionError('')}
                className="btn btn-xs btn-circle btn-ghost text-white hover:bg-black/20 shrink-0"
              >
                ✕
              </button>
            </div>
          )}

          <form className="space-y-3" onSubmit={handleLeadSubmit} noValidate>
            {/* First & Last Name in One Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label py-1">
                  <span className="label-text font-semibold text-xs text-base-content">First Name <span className="text-error">*</span></span>
                </label>
                <input
                  required
                  disabled={isSubmitting}
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  value={leadForm.firstName}
                  onChange={handleLeadChange}
                  className={`input input-bordered input-sm w-full text-xs ${
                    leadErrors.firstName ? 'input-error' : ''
                  }`}
                />
                {leadErrors.firstName && (
                  <span className="text-xs text-error mt-1 block">{leadErrors.firstName}</span>
                )}
              </div>

              <div>
                <label className="label py-1">
                  <span className="label-text font-semibold text-xs text-base-content">Last Name</span>
                </label>
                <input
                  disabled={isSubmitting}
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  value={leadForm.lastName}
                  onChange={handleLeadChange}
                  className="input input-bordered input-sm w-full text-xs"
                />
              </div>
            </div>

            <div>
              <label className="label py-1">
                <span className="label-text font-semibold text-xs text-base-content">Phone <span className="text-error">*</span></span>
              </label>
              <input
                required
                disabled={isSubmitting}
                type="tel"
                inputMode="tel"
                name="phone"
                maxLength={14}
                placeholder="(555) 000-0000"
                value={leadForm.phone}
                onChange={handleLeadChange}
                className={`input input-bordered input-sm w-full text-xs ${
                  leadErrors.phone ? 'input-error' : ''
                }`}
              />
              {leadErrors.phone && (
                <span className="text-xs text-error mt-1 block">{leadErrors.phone}</span>
              )}
            </div>

            <div>
              <label className="label py-1">
                <span className="label-text font-semibold text-xs text-base-content">Email <span className="text-error">*</span></span>
              </label>
              <input
                required
                disabled={isSubmitting}
                type="email"
                inputMode="email"
                name="email"
                placeholder="name@example.com"
                value={leadForm.email}
                onChange={handleLeadChange}
                className={`input input-bordered input-sm w-full text-xs ${
                  leadErrors.email ? 'input-error' : ''
                }`}
              />
              {leadErrors.email && (
                <span className="text-xs text-error mt-1 block">{leadErrors.email}</span>
              )}
            </div>

            {/* Mandatory TCPA Checkbox */}
            <div className="pt-2 border-t border-base-200">
              <label className="label cursor-pointer flex items-start space-x-2">
                <input
                  type="checkbox"
                  disabled={isSubmitting}
                  name="tcpaConsent"
                  checked={leadForm.tcpaConsent}
                  onChange={handleLeadChange}
                  className={`checkbox checkbox-primary checkbox-sm rounded mt-0.5 ${
                    leadErrors.tcpaConsent ? 'checkbox-error' : ''
                  }`}
                />
                <span className="label-text text-[11px] leading-relaxed text-base-content/85">
                  I agree to the{' '}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isSubmitting) openLegalModal('terms');
                    }}
                    className="underline font-bold text-primary hover:text-primary-focus cursor-pointer inline text-left bg-transparent border-0 p-0"
                  >
                    Terms &amp; Conditions
                  </button>
                  ,{' '}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isSubmitting) openLegalModal('privacy');
                    }}
                    className="underline font-bold text-primary hover:text-primary-focus cursor-pointer inline text-left bg-transparent border-0 p-0"
                  >
                    Privacy Policy
                  </button>
                  , and{' '}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!isSubmitting) openLegalModal('comm');
                    }}
                    className="underline font-bold text-primary hover:text-primary-focus cursor-pointer inline text-left bg-transparent border-0 p-0"
                  >
                    Communication Agreement
                  </button>
                  . By checking this box and clicking "Reveal My Savings", I provide my express written consent for Insurance Enterprise USA to contact me regarding health insurance options via email, phone calls, and text messages at the number provided above, including automated technology. <strong className="text-base-content">Consent is not a condition of purchase.</strong>
                </span>
              </label>
              {leadErrors.tcpaConsent && (
                <span className="text-xs text-error mt-1 block font-semibold">{leadErrors.tcpaConsent}</span>
              )}
            </div>

            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-base-200">
              <button
                type="button"
                disabled={isSubmitting}
                onClick={() => {
                  if (!isSubmitting) setShowModal(false);
                }}
                className={`btn btn-ghost btn-sm text-xs ${
                  isSubmitting ? 'btn-disabled opacity-30 cursor-not-allowed pointer-events-none' : ''
                }`}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!leadForm.tcpaConsent || isSubmitting}
                className={`btn btn-sm px-6 text-xs font-black uppercase tracking-wider transition-all ${
                  leadForm.tcpaConsent && !isSubmitting
                    ? 'btn-secondary text-white shadow-md hover:scale-[1.01] active:scale-[0.99]'
                    : isSubmitting
                    ? 'btn-secondary text-white opacity-85 cursor-wait'
                    : 'btn-disabled opacity-40 bg-base-300 text-base-content/40 cursor-not-allowed'
                }`}
              >
                {isSubmitting ? (
                  <span className="inline-flex items-center space-x-2">
                    <span className="loading loading-spinner loading-xs"></span>
                    <span>Submitting...</span>
                  </span>
                ) : (
                  'REVEAL MY SAVINGS'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Unified 3-Tab Legal Agreements & Disclosures Modal */}
      <Modal open={showLegalModal} handleClose={() => setShowLegalModal(false)} closeButton={true} maxWidth="sm:max-w-2xl">
        <div className="p-4 sm:p-6 space-y-4 text-left w-full">
          {/* Header */}
          <div className="border-b border-base-200 pb-3">
            <h3 className="text-base sm:text-lg font-bold text-base-content">
              Legal Agreements &amp; Disclosures
            </h3>
            <p className="text-xs text-base-content/60">
              Please review our terms, privacy policy, and communications consent.
            </p>
          </div>

          {/* 3 Tabs Bar */}
          <div className="flex border-b border-base-200 gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setLegalTab('terms')}
              className={`py-2 px-3 sm:px-4 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'terms'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Terms &amp; Conditions
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('privacy')}
              className={`py-2 px-3 sm:px-4 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'privacy'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Privacy Policy
            </button>
            <button
              type="button"
              onClick={() => setLegalTab('comm')}
              className={`py-2 px-3 sm:px-4 text-xs font-bold transition-all rounded-t-lg border-b-2 ${
                legalTab === 'comm'
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-transparent text-base-content/70 hover:text-base-content hover:bg-base-200/50'
              }`}
            >
              Communication Agreement
            </button>
          </div>

          {/* Scrollable Tab Content Container */}
          <div className="modal-legal-content max-h-[380px] overflow-y-auto pr-2 text-xs text-base-content/85 leading-relaxed bg-base-200/30 p-3 sm:p-4 rounded-xl border border-base-200">
            {legalTab === 'terms' && (
              <div className="space-y-4">
                <TermsAndConditionContent />
              </div>
            )}

            {legalTab === 'privacy' && (
              <div className="space-y-4">
                <PrivacyPolicyContent />
              </div>
            )}

            {legalTab === 'comm' && (
              <div className="space-y-4">
                <div className="border-b border-base-200 pb-2">
                  <h4 className="text-sm font-bold text-base-content">
                    Electronic Communications Agreement &amp; TCPA Consent
                  </h4>
                  <p className="text-[11px] text-base-content/60">
                    Effective Date: August 4, 2026 • insuranceenterpriseusa.com
                  </p>
                </div>

                <p>
                  By submitting your contact information (including your phone number and email address) through any online quote form, health plan calculator, enrollment form, or inquiry portal on this Site, you provide your express written consent and agreement to the following terms:
                </p>

                <div className="space-y-3">
                  <div>
                    <h5 className="font-bold text-base-content">1. Express Written Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You authorize Insurance Enterprise USA, its licensed agents, affiliates, and representatives to contact you regarding health insurance quotes, plan options, coverage details, and related insurance services via telephone calls, SMS/MMS text messages, and email at the phone number and email address provided.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">2. Automated Technology</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You understand and agree that communications may be transmitted using automated telephone dialing systems (ATDS), artificial or prerecorded voice messages, automated SMS systems, and computer-generated emails.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">3. Consent Not Required for Purchase</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Your consent to receive promotional and informational communications is completely voluntary and is <strong>not a condition of purchasing</strong> any goods, services, or insurance policies from Insurance Enterprise USA.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">4. Message Frequency &amp; Carrier Rates</h5>
                    <p className="text-base-content/80 mt-0.5">
                      Message frequency varies depending on your inquiries and active requests. Standard message and data rates charged by your mobile wireless carrier may apply to all SMS/MMS messages.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">5. Opt-Out &amp; Revocation of Consent</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You may revoke your consent or opt out at any time. To stop receiving text messages, reply <strong>STOP</strong> to any SMS received. To request help, reply <strong>HELP</strong>. To unsubscribe from emails, click the unsubscribe link at the bottom of any email. You may also contact our compliance department at <span className="font-bold text-primary">888-350-6605</span> or via email at <span className="font-bold text-primary">enroll@insuranceenterpriseusa.com</span>.
                    </p>
                  </div>

                  <div>
                    <h5 className="font-bold text-base-content">6. E-SIGN Disclosure</h5>
                    <p className="text-base-content/80 mt-0.5">
                      You consent to receive all notices, policy documents, disclosures, and communications electronically pursuant to the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer with Agreement Auto-Check Button */}
          <div className="pt-4 border-t border-base-200 space-y-3">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-base-content/60 text-center sm:text-left">
                By clicking agree, your consent checkbox will automatically be checked.
              </p>
              <div className="flex items-center space-x-3 w-full sm:w-auto justify-end">
                <button
                  type="button"
                  onClick={() => setShowLegalModal(false)}
                  className="btn btn-ghost btn-sm sm:btn-md px-4 text-xs font-semibold"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setLeadForm((prev) => ({ ...prev, tcpaConsent: true }));
                    if (leadErrors.tcpaConsent) {
                      setLeadErrors((prev) => ({ ...prev, tcpaConsent: '' }));
                    }
                    setShowLegalModal(false);
                  }}
                  className="btn btn-secondary btn-sm sm:btn-md px-6 sm:px-8 text-white font-bold text-xs sm:text-sm shadow-md hover:scale-[1.02] active:scale-[0.98] transition-all whitespace-nowrap"
                >
                  I Agree
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Floating Success Toast Notification */}
      {showSuccessToast && (
        <div className="toast toast-top toast-center z-[9999] animate-fadeIn">
          <div className="alert bg-[#2AA107] text-white shadow-2xl rounded-2xl flex items-center space-x-3 py-3 px-5 border-0">
            <FaCheckCircle className="text-xl shrink-0" />
            <div className="text-left">
              <div className="font-bold text-xs sm:text-sm">Information Saved Successfully!</div>
              <div className="text-[11px] text-white/90">Your custom savings analysis has been generated below.</div>
            </div>
          </div>
        </div>
      )}

      {/* Floating Error Toast Notification */}
      <AlertError showError={showErrorToast} onClose={() => setShowErrorToast(false)}>
        {errorToastMsg}
      </AlertError>
    </div>
  );
};

export default HealthPlanCalculator;
